import { useState } from "react";
import { enGB, et } from "date-fns/locale";
import type { DateRange } from "react-day-picker";
import { Activity, CalendarDays, FileType, SlidersHorizontal, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { CONTROL_ACTIVE_CLASS } from "@/components/ui/control-scale";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import {
  FilterOptionRow,
  FilterPanelBody,
  FilterPanelContent,
  FilterPanelHeader,
  FilterSection,
  FilterSections,
} from "@/components/filters/FilterPanel";
import { FilterChips } from "@/components/filters/FilterChips";
import { singleValueChip, type FilterChip } from "@/components/filters/filterChip";
import {
  ALL_HISTORY_ACTORS,
  ALL_HISTORY_FILE_TYPES,
  UNKNOWN_HISTORY_FILE_TYPE,
  type DocumentHistoryActorOption,
  type DocumentHistoryDateFilter as DateFilterValue,
  type DocumentHistoryDatePreset,
  type DocumentHistoryFilter,
} from "@/domains/documents/history/documentHistoryPresentation";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { DocumentTranslations } from "@/lib/i18n/types/documents";
import { cn } from "@/lib/utils";

type HistoryCopy = DocumentTranslations["log"]["history"];
type DateFilterCopy = HistoryCopy["dateFilter"];
type Dimension = "event" | "actor" | "fileType" | "date";

const PRESETS: DocumentHistoryDatePreset[] = ["today", "7d", "30d"];

export interface DocumentHistoryFilterState {
  filter: DocumentHistoryFilter;
  setFilter: (value: DocumentHistoryFilter) => void;
  actor: string;
  setActor: (value: string) => void;
  fileType: string;
  setFileType: (value: string) => void;
  dateFilter: DateFilterValue | null;
  setDateFilter: (value: DateFilterValue | null) => void;
  availableFilters: DocumentHistoryFilter[];
  availableActors: DocumentHistoryActorOption[];
  availableFileTypes: string[];
}

interface DocumentHistoryFilterControlProps {
  filters: DocumentHistoryFilterState;
  copy: HistoryCopy;
  language: string;
}

function formatDay(date: Date, language: string): string {
  return date.toLocaleDateString(language === "et" ? "et-EE" : "en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function presetLabel(preset: DocumentHistoryDatePreset, copy: DateFilterCopy): string {
  if (preset === "today") return copy.today;
  return preset === "7d" ? copy.last7Days : copy.last30Days;
}

function dateFilterLabel(value: DateFilterValue, copy: DateFilterCopy, language: string): string {
  if (value.kind === "preset") return presetLabel(value.preset, copy);
  const [from, to] = value.from <= value.to ? [value.from, value.to] : [value.to, value.from];
  if (from.toDateString() === to.toDateString()) return formatDay(from, language);
  return `${formatDay(from, language)} – ${formatDay(to, language)}`;
}

/**
 * The Document history panel's filters, on the shared filter panel every page
 * uses: activity, person, file type and date as sections, each one choice at a
 * time, with the active ones as removable chips left of the button. A section
 * is offered only when it can narrow something — one person or one file type
 * in the loaded history would be a control with a single answer. The date is
 * always offered: presets that always mean "up to now", plus a calendar for a
 * custom range of whole local days.
 */
export function DocumentHistoryFilterControl({
  filters,
  copy,
  language,
}: DocumentHistoryFilterControlProps) {
  const { t } = useLanguage();
  const dateCopy = copy.dateFilter;
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState<string | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange | undefined>();

  const fileTypeLabel = (value: string) =>
    value === UNKNOWN_HISTORY_FILE_TYPE ? copy.unknownFileType : value;
  const actorLabel = (option: DocumentHistoryActorOption) => option.name ?? copy.unknownUser;
  const eventChoices = filters.availableFilters.filter((value) => value !== "all");

  const dimensions: Dimension[] = [
    ...(eventChoices.length > 1 ? (["event"] as const) : []),
    ...(filters.availableActors.length > 1 ? (["actor"] as const) : []),
    ...(filters.availableFileTypes.length > 1 ? (["fileType"] as const) : []),
    "date",
  ];
  const dimensionLabel: Record<Dimension, string> = {
    event: copy.filterSections.activity,
    actor: copy.filterSections.person,
    fileType: copy.filterSections.fileType,
    date: copy.filterSections.date,
  };
  const dimensionIcon = { event: Activity, actor: Users, fileType: FileType, date: CalendarDays };

  const clearDimension = (d: Dimension) => {
    if (d === "event") filters.setFilter("all");
    else if (d === "actor") filters.setActor(ALL_HISTORY_ACTORS);
    else if (d === "fileType") filters.setFileType(ALL_HISTORY_FILE_TYPES);
    else filters.setDateFilter(null);
  };
  const isActive = (d: Dimension) =>
    d === "event"
      ? filters.filter !== "all"
      : d === "actor"
        ? filters.actor !== ALL_HISTORY_ACTORS
        : d === "fileType"
          ? filters.fileType !== ALL_HISTORY_FILE_TYPES
          : filters.dateFilter !== null;

  const selectedActor = filters.availableActors.find((option) => option.key === filters.actor);
  const chipText: Record<Dimension, () => string> = {
    event: () => copy.filters[filters.filter],
    actor: () => (selectedActor ? actorLabel(selectedActor) : copy.unknownUser),
    fileType: () => fileTypeLabel(filters.fileType),
    date: () => (filters.dateFilter ? dateFilterLabel(filters.dateFilter, dateCopy, language) : ""),
  };
  const chips: FilterChip<Dimension>[] = dimensions
    .filter(isActive)
    .map((d) => singleValueChip(d, chipText[d](), () => clearDimension(d), dimensionLabel[d]));
  const clearAll = () => dimensions.forEach(clearDimension);

  const openAt = (d: Dimension) => {
    setSection(d);
    if (d === "date" && filters.dateFilter?.kind === "custom") openCalendar();
    setOpen(true);
  };
  // Seed the calendar with the applied range so re-opening it edits, not restarts.
  const openCalendar = () => {
    setDraft(
      filters.dateFilter?.kind === "custom"
        ? { from: filters.dateFilter.from, to: filters.dateFilter.to }
        : undefined
    );
    setCalendarOpen(true);
  };
  const closePanel = () => {
    setOpen(false);
    setSection(null);
    setCalendarOpen(false);
  };
  const applyCustom = () => {
    if (!draft?.from) return;
    filters.setDateFilter({ kind: "custom", from: draft.from, to: draft.to ?? draft.from });
    closePanel();
  };

  // One choice per section: ticking the chosen row again returns it to "all".
  const choiceRow = (
    d: Dimension,
    key: string,
    label: string,
    checked: boolean,
    pick: () => void
  ) => (
    <FilterOptionRow
      key={key}
      label={label}
      ariaLabel={`${dimensionLabel[d]}: ${label}`}
      checked={checked}
      onToggle={() => (checked ? clearDimension(d) : pick())}
      single
    />
  );

  const sectionBody = (d: Dimension) => {
    if (d === "event") {
      return eventChoices.map((value) =>
        choiceRow(d, value, copy.filters[value], filters.filter === value, () =>
          filters.setFilter(value)
        )
      );
    }
    if (d === "actor") {
      return filters.availableActors.map((option) =>
        choiceRow(d, option.key, actorLabel(option), filters.actor === option.key, () =>
          filters.setActor(option.key)
        )
      );
    }
    if (d === "fileType") {
      return filters.availableFileTypes.map((value) =>
        choiceRow(d, value, fileTypeLabel(value), filters.fileType === value, () =>
          filters.setFileType(value)
        )
      );
    }
    const custom = filters.dateFilter?.kind === "custom" ? filters.dateFilter : null;
    return (
      <>
        {PRESETS.map((preset) =>
          choiceRow(
            d,
            preset,
            presetLabel(preset, dateCopy),
            filters.dateFilter?.kind === "preset" && filters.dateFilter.preset === preset,
            () => {
              setCalendarOpen(false);
              filters.setDateFilter({ kind: "preset", preset });
            }
          )
        )}
        <FilterOptionRow
          label={custom ? dateFilterLabel(custom, dateCopy, language) : dateCopy.customRange}
          ariaLabel={`${dimensionLabel.date}: ${dateCopy.customRange}`}
          checked={custom !== null}
          single
          // Ticked: unticking clears the range, like any other choice. Edit an
          // applied range from its chip.
          onToggle={() => {
            if (custom) {
              setCalendarOpen(false);
              filters.setDateFilter(null);
            } else if (calendarOpen) setCalendarOpen(false);
            else openCalendar();
          }}
        />
        {calendarOpen && (
          <div>
            <div className="flex justify-center">
              <Calendar
                mode="range"
                locale={language === "et" ? et : enGB}
                selected={draft}
                onSelect={setDraft}
                defaultMonth={draft?.from}
                disabled={{ after: new Date() }}
                numberOfMonths={1}
              />
            </div>
            <div className="flex justify-end px-3 py-2">
              <Button size="sm" onClick={applyCustom} disabled={!draft?.from}>
                {dateCopy.apply}
              </Button>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
      <FilterChips chips={chips} onOpenDimension={openAt} onClearAll={clearAll} />
      <Popover open={open} onOpenChange={(next) => (next ? setOpen(true) : closePanel())}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="control"
            aria-label={
              chips.length > 0
                ? `${t.documents.filters.title} (${chips.length})`
                : t.documents.filters.title
            }
            className={cn("shrink-0 gap-1.5", chips.length > 0 && CONTROL_ACTIVE_CLASS)}
          >
            <SlidersHorizontal className="opacity-60" />
            {t.documents.filters.title}
            {chips.length > 0 && (
              <span
                aria-hidden="true"
                className="inline-flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold tabular-nums text-primary-foreground"
              >
                {chips.length}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <FilterPanelContent align="end">
          <FilterPanelHeader
            title={t.common.filterPanel.active}
            activeCount={chips.length}
            clearLabel={t.common.filterPanel.clearAll}
            onClearAll={clearAll}
          />
          <FilterPanelBody>
            <FilterSections value={section} onValueChange={setSection}>
              {dimensions.map((d) => (
                <FilterSection
                  key={d}
                  value={d}
                  icon={dimensionIcon[d]}
                  label={dimensionLabel[d]}
                  selectedCount={Number(isActive(d))}
                  onClear={() => clearDimension(d)}
                  clearLabel={t.common.filterPanel.clearAll}
                  // The calendar is taller than the usual option cap.
                  contentClassName={d === "date" ? "max-h-none" : undefined}
                  singleChoice
                >
                  {sectionBody(d)}
                </FilterSection>
              ))}
            </FilterSections>
          </FilterPanelBody>
        </FilterPanelContent>
      </Popover>
    </div>
  );
}
